import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useProfile } from './ProfileContext';
import { supabase } from '../lib/supabase';

const AudioSettingsContext = createContext({});

export const useAudioSettings = () => useContext(AudioSettingsContext);

const DEFAULTS = {
  musicEnabled: true,
  soundEnabled: true,
  mainVolume: 0.8,
};

// Map DB column names to context keys
const DB_TO_KEY = {
  pref_music_enabled: 'musicEnabled',
  pref_sound_enabled: 'soundEnabled',
  pref_main_volume: 'mainVolume',
};

const KEY_TO_DB = Object.fromEntries(
  Object.entries(DB_TO_KEY).map(([db, key]) => [key, db])
);

/**
 * What this device last knew, before the account's own settings arrive.
 *
 * The defaults say music on. The account's answer takes a round trip. In
 * between, the music started at full volume and was cut off a moment later
 * for everyone who had turned it off: a blast, then silence, on every launch.
 * The last saved answer is on this device already, so start from that.
 */
const fromDevice = () => {
  const read = (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  };
  const music = read('musicEnabled');
  const sound = read('soundEnabled');
  return {
    ...DEFAULTS,
    ...(music != null ? { musicEnabled: music !== 'false' } : {}),
    ...(sound != null ? { soundEnabled: sound !== 'false' } : {}),
  };
};

/**
 * Each profile's last known answers, kept on this device.
 *
 * A family shares a phone. Switching from a child who likes the music to a
 * parent who does not used to keep the child's music playing until the
 * parent's settings arrived from the server. With the answers cached per
 * profile, the switch is right on the first frame.
 */
const cacheKey = (profileId) => `cc:audio:${profileId}`;
const readCache = (profileId) => {
  if (!profileId) return null;
  try {
    const raw = localStorage.getItem(cacheKey(profileId));
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? { ...DEFAULTS, ...parsed } : null;
  } catch { return null; }
};
const writeCache = (profileId, values) => {
  if (!profileId) return;
  try { localStorage.setItem(cacheKey(profileId), JSON.stringify(values)); } catch { /* fine */ }
  try {
    localStorage.setItem('musicEnabled', String(values.musicEnabled));
    localStorage.setItem('soundEnabled', String(values.soundEnabled));
  } catch { /* fine */ }
};

export const AudioSettingsProvider = ({ children }) => {
  const { user } = useAuth();
  const { userProfile } = useProfile();
  const [settings, setSettings] = useState(fromDevice);
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef(null);
  const pendingSaveRef = useRef({});

  // Load preferences from Supabase when user logs in
  useEffect(() => {
    if (!userProfile?.id) {
      setSettings(fromDevice());
      setLoaded(false);
      return;
    }

    // This profile's own last answers, before the server is asked at all.
    const cached = readCache(userProfile.id);
    setSettings(cached || fromDevice());
    if (cached) writeCache(userProfile.id, cached);
    setLoaded(false);

    const loadSettings = async () => {
      const columns = Object.keys(DB_TO_KEY).join(',');
      const { data, error } = await supabase
        .from('users')
        .select(columns)
        .eq('id', userProfile.id)
        .single();

      if (error || !data) {
        setSettings({ ...DEFAULTS });
        localStorage.setItem('musicEnabled', DEFAULTS.musicEnabled.toString());
        localStorage.setItem('soundEnabled', DEFAULTS.soundEnabled.toString());
        setLoaded(true);
        return;
      }

      const loaded = { ...DEFAULTS };
      for (const [dbCol, key] of Object.entries(DB_TO_KEY)) {
        if (data[dbCol] !== null && data[dbCol] !== undefined) {
          loaded[key] = data[dbCol];
        }
      }

      setSettings(loaded);
      writeCache(userProfile.id, loaded);
      setLoaded(true);
    };

    loadSettings();
  }, [userProfile?.id]);

  // Debounced save to Supabase
  const flushSave = useCallback(async () => {
    if (!userProfile?.id) return;
    const updates = { ...pendingSaveRef.current };
    pendingSaveRef.current = {};

    if (Object.keys(updates).length === 0) return;

    // Convert keys to DB column names
    const dbUpdates = {};
    for (const [key, value] of Object.entries(updates)) {
      const dbCol = KEY_TO_DB[key];
      if (dbCol) {
        dbUpdates[dbCol] = value;
      }
    }

    await supabase
      .from('users')
      .update(dbUpdates)
      .eq('id', userProfile.id);
  }, [userProfile?.id]);

  const updateSetting = useCallback((key, value) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      writeCache(userProfile?.id, next);
      return next;
    });

    // Sync music/sound to localStorage for BackgroundMusic compatibility
    if (key === 'musicEnabled') {
      localStorage.setItem('musicEnabled', value.toString());
    }
    if (key === 'soundEnabled') {
      localStorage.setItem('soundEnabled', value.toString());
    }

    // Queue for debounced save
    pendingSaveRef.current[key] = value;

    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(flushSave, 500);
  }, [flushSave, userProfile?.id]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setSettings({ ...DEFAULTS });
    localStorage.setItem('musicEnabled', DEFAULTS.musicEnabled.toString());
    localStorage.setItem('soundEnabled', DEFAULTS.soundEnabled.toString());

    // Save all defaults to DB
    pendingSaveRef.current = { ...DEFAULTS };
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(flushSave, 500);
  }, [flushSave]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        flushSave();
      }
    };
  }, [flushSave]);

  // Reset on logout
  useEffect(() => {
    if (!user) {
      setSettings({ ...DEFAULTS });
      setLoaded(false);
      pendingSaveRef.current = {};
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    }
  }, [user]);

  const value = {
    settings,
    loaded,
    updateSetting,
    resetToDefaults,
    DEFAULTS,
  };

  return (
    <AudioSettingsContext.Provider value={value}>
      {children}
    </AudioSettingsContext.Provider>
  );
};
